// QuestionnaireEditorContainer.tsx
import React, { useEffect, useState, useRef, useCallback, useMemo } from "react"
import { isAxiosError } from "axios"
import Split from "react-split"
import { useProjectStore } from "stores/projectStore"
import { useUserStore } from "stores/userStore"
import { useQuestionnaire } from "features/QuestionnaireEditor/api/useQuestionnaire"
import { useEngine } from "hooks/api/useEngine"
import PreviewQuestionnaire from "features/QuestionnaireEditor/components/Preview/PreviewQuestionnaire.component"
import VariantsMenuBar from "features/QuestionnaireEditor/components/Variant/VariantsMenuBar.component"
import { Loader } from "shared/ui/Loader/Loader.component"
import Editor from "features/QuestionnaireEditor/components/Editor/Editor.component"
import { notify } from "shared/ui/Notifications/notify"
import {
  parseMasterLanguages,
  parseQuestionnaireVariant,
  updateQuestionnaireWithVariant,
} from "utils/variantUtils"
import { escapeHTMLPreserveFormatting } from "utils/escapeHtml"
import { htmlToPlainText, stripCommentSpans } from "utils/editorUtils"
import handleSequenceError from "utils/handleSequenceError"
import { failureOf } from "utils/errMsgOf"
import { HandledApiError } from "api/queryClient"
import { useCollaboration } from "features/QuestionnaireEditor/hooks/useCollaboration"
import {
  Variant,
  SelectedVariant,
  EditorRef,
  LoadedQuestionnaire,
  NotificationError,
  NotificationWarning,
  EditorSaveOptions,
} from "types/questionnaire.types"
import { StagedBlock } from "types/template.types"
import { ConversionOptions } from "types/engineExport.types"
import {
  AssistantPanel,
  AssistantScreenProvider,
  AssistantScreenRegistrar,
  AssistantScreenRegistration,
  EDITOR_RECONNECTING_TEXT,
  ProposalOf,
  QnrProposalReview,
  editorLines,
  noEditMatches,
  selectConversation,
  selectionLines,
  useAssistantStatus,
  useAssistantStore,
} from "features/Assistant"

/**
 * Converts questionnaire text to the editor's HTML: one paragraph per line, with the
 * bold and italic tags kept as formatting.
 *
 * @param text - The questionnaire text
 * @returns The editor HTML
 */
const toEditorHTML = (text: string): string =>
  text
    .split("\n")
    .map((s: string) =>
      s.trim() === "" ? "<p></p>" : "<p>" + escapeHTMLPreserveFormatting(s) + "</p>"
    )
    .join("")

/** A save that failed: the text sent, whether it was a checkpoint, and how to read the text back. */
interface FailedSave {
  text: string
  checkpoint?: boolean
  readBack: () => Promise<string>
}

/**
 * Whether the server holds the text a failed save sent: a checkpoint's error answer means either
 * the text wasn't saved or only its version wasn't recorded, and reading the text back tells
 * which. A read that fails counts as not saved.
 */
const textWasSaved = async ({ text, readBack }: FailedSave): Promise<boolean> => {
  try {
    return (await readBack()) === text
  } catch {
    return false
  }
}

/**
 * Reports a failed save once: a server error was already reported with the server's message,
 * an error answer is reported with its message, anything else gets a generic message here. A
 * checkpoint whose text was saved but whose version wasn't recorded says so, not that the save
 * failed. Returns a HandledApiError to throw, so the caller knows the failure was reported.
 *
 * @param error - What the save threw
 * @param save - The save that failed
 * @returns The error to rethrow
 */
const reportSaveFailure = async (error: unknown, save: FailedSave): Promise<HandledApiError> => {
  console.error("Error Saving Data:", error)
  if (error instanceof HandledApiError) return error
  const answered = isAxiosError(error) && !!error.response
  const message = answered ? failureOf(error) : "Please try again later."
  if (answered && save.checkpoint && (await textWasSaved(save))) {
    notify.error(
      "Version not recorded",
      `The questionnaire was saved, but this version wasn't added to the history. ${message}`
    )
    return new HandledApiError("The version wasn't recorded")
  }
  notify.error("Error saving questionnaire", message)
  return new HandledApiError("The questionnaire wasn't saved")
}

/**
 * `QuestionnaireEditorContainer` is a component that manages the questionnaire editing interface.
 * It includes an editor, a preview pane, and a variant management toolbar.
 *
 * @component
 * @returns The rendered `QuestionnaireEditorContainer` component
 */
const QuestionnaireEditorContainer: React.FC = () => {
  const project = useProjectStore((state) => state.project)
  const user = useUserStore((state) => state.user)
  const {
    getQuestionnaireByID,
    getQuestionnaireSilent,
    isLoadingQuestionnaire,
    saveQuestionnaireByID,
  } = useQuestionnaire()
  const { convertToWordPreview, isConvertingToWordPreview } = useEngine()

  // State variables with proper TypeScript types
  const [questionnaire, setQuestionnaire] = useState<string>("")
  const [previewVisible, setPreviewVisible] = useState<boolean>(false)
  const [previewContent, setPreviewContent] = useState<string>("")
  const [variants, setVariants] = useState<Variant[]>([])
  const [selectedVariant, setSelectedVariant] = useState<SelectedVariant>({})
  const [fullQuestionnaire, setFullQuestionnaire] = useState<string>("")
  const [masterQuestionnaire, setMasterQuestionnaire] = useState<string>("")
  const [variantContent, setVariantContent] = useState<string>("")
  const [labelToJump, setLabelToJump] = useState<string | null>(null)
  const [isLabelsModalVisible, setIsLabelsModalVisible] =
    useState<boolean>(false)
  const [pendingScrollLine, setPendingScrollLine] = useState<number | null>(
    null
  )
  const [checkingQuestionnaireEnabled, setCheckingQuestionnaireEnabled] =
    useState<boolean>(false)
  const [checkQuestionnaireErrors, setCheckQuestionnaireErrors] = useState<
    NotificationError[]
  >([])
  const [checkQuestionnaireWarnings, setCheckQuestionnaireWarnings] = useState<
    NotificationWarning[]
  >([])
  const [isEditorConnecting, setIsEditorConnecting] = useState<boolean>(false)

  const editorRef = useRef<EditorRef>(null)
  const labelsButtonRef = useRef<HTMLElement>(null)

  // The assistant: its panel takes the Preview's place while open (one or the other)
  const { enabled: assistantEnabled } = useAssistantStatus()
  const assistantOpen =
    useAssistantStore((s) => selectConversation(project.id)(s).open) && assistantEnabled
  const setAssistantOpen = useAssistantStore((s) => s.setOpen)
  const [reviewing, setReviewing] = useState<ProposalOf<"qnr-edit"> | null>(null)
  // Filled by the menu bar: opens the Templates wizard filled in with a block
  const templateProposalRef = useRef<
    ((block: StagedBlock, onStaged?: () => void) => void) | null
  >(null)
  const currentVariant: string = (selectedVariant as Variant).name || ""

  useEffect(() => {
    if (assistantOpen) setPreviewVisible(false)
  }, [assistantOpen])

  /** Shows or hides the Preview; showing it closes the assistant. */
  const showPreview = useCallback(
    (visible: boolean): void => {
      setPreviewVisible(visible)
      if (visible) setAssistantOpen(project.id, false)
    },
    [setAssistantOpen, project.id]
  )

  const getTiptapEditor = useCallback(() => editorRef.current?.getEditor() ?? null, [])

  /**
   * The editor's registration with the assistant: the open variant, its lines
   * and the selected lines go with each turn, and each proposal kind the
   * editor reviews has its handler here.
   */
  const assistantRegistration: AssistantScreenRegistration = {
    screen: "editor",
    getContext: () => {
      const editor = getTiptapEditor()
      if (!editor) return {}
      const selection = selectionLines(editor)
      return {
        qnr: {
          variant: currentVariant,
          lines: editorLines(editor),
          ...(selection ? { selection } : {}),
        },
      }
    },
    // The cards ask blockedReason again when the connection state changes
    revision: isEditorConnecting,
    handlers: {
      "qnr-edit": {
        review: (p) => {
          if (p.kind === "qnr-edit") setReviewing(p)
        },
        // None of its edits matches the open variant any more (another
        // variant's proposal can't be told here; its review says to switch)
        isStale: (p) => {
          if (p.kind !== "qnr-edit" || p.variant !== currentVariant) return false
          const editor = getTiptapEditor()
          return !!editor && noEditMatches(editor, p.edits)
        },
        // Read-only while the connection is down: nothing can go in until it is back
        blockedReason: () => {
          const editor = getTiptapEditor()
          return editor && !editor.isEditable ? EDITOR_RECONNECTING_TEXT : null
        },
      },
      // Opens the Templates dialog at the template's wizard, filled in with
      // the proposal's block; the user adds it to the tray and inserts as
      // usual. Adding it to the tray accepts the proposal.
      "template-fill": {
        review: (p) => {
          if (p.kind !== "template-fill") return
          templateProposalRef.current?.({ ...p.block, id: "assistant-" + p.id }, () =>
            useAssistantStore.getState().setProposalStatus(project.id, p.id, "accepted")
          )
        },
        // As from the menu, templates go into Master only
        blockedReason: () =>
          currentVariant === "" ? null : "Templates are added in Master",
      },
    },
  }

  // Handle variant changes from collaboration (other browsers creating/deleting variants)
  const handleCollaborativeVariantChange = useCallback(
    (newVariants: Variant[]) => {
      // Merge with existing variants - new variants from collaboration won't have content yet
      setVariants((prevVariants) => {
        const existingNames = new Set(prevVariants.map((v) => v.name))
        const newNames = new Set(newVariants.map((v) => v.name))

        // Keep existing variants that are still in the new list (preserve their content)
        const kept = prevVariants.filter((v) => newNames.has(v.name))

        // Add new variants that don't exist yet
        const added = newVariants.filter((v) => !existingNames.has(v.name))

        return [...kept, ...added]
      })
    },
    []
  )

  // Initialize collaboration for variant metadata sync
  const { updateVariantMetadata, removeVariantMetadata } = useCollaboration({
    projectId: project.id,
    userId: user.username,
    onVariantChange: handleCollaborativeVariantChange,
  })

  // Master's own languages, from the saved text as the variants are: the test survey offers them on Master.
  const masterLanguages: string[] = useMemo(
    () => parseMasterLanguages(fullQuestionnaire),
    [fullQuestionnaire]
  )

  const inMaster: boolean =
    Object.keys(selectedVariant).length === 0 &&
    selectedVariant.constructor === Object

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const selectedVariantWithName = selectedVariant as Variant
      if (!selectedVariantWithName.name) {
        await getQuestionnaire(project.id)
      }
    }
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariant])

  /**
   * Loads a full questionnaire text into the editor state: the variants are split out into
   * the variant list and only the master goes to the editor, with its bold and italic kept.
   * Used when the questionnaire is fetched and when the Editor restores a version.
   *
   * @function loadQuestionnaireText
   * @param text - The full questionnaire text, variants included
   * @returns The master and each variant as editor HTML
   */
  const loadQuestionnaireText = useCallback((text: string): LoadedQuestionnaire => {
    setFullQuestionnaire(text)
    const parsedVariants: Variant[] = parseQuestionnaireVariant(text)
    setVariants(parsedVariants)

    const masterContentLines: string[] = []
    let insideVariant = false

    text.split("\n").forEach((line: string) => {
      if (line.startsWith("#DEFINE VARIANT")) {
        insideVariant = true
      } else if (insideVariant && line.trim() === "") {
        return
      } else if (insideVariant && !line.startsWith("#")) {
        return
      } else if (!insideVariant) {
        masterContentLines.push(line)
      }
    })

    const masterContent: string = masterContentLines.join("\n")
    setMasterQuestionnaire(masterContent)
    const masterHTML: string = toEditorHTML(masterContent)
    setQuestionnaire(masterHTML)
    return {
      masterHTML,
      variants: parsedVariants.map((variant: Variant) => ({
        ...variant,
        html: toEditorHTML(variant.content),
      })),
    }
  }, [])

  /**
   * Fetches the questionnaire by ID and processes its content to extract master and variant data.
   *
   * @async
   * @function getQuestionnaire
   * @param id - The ID of the questionnaire
   * @returns Promise that resolves when the questionnaire is successfully fetched and processed
   */
  const getQuestionnaire = async (id: string): Promise<void> => {
    try {
      const res = await getQuestionnaireByID(id, project.Created_by)
      if (!res.data) {
        setQuestionnaire("")
        setFullQuestionnaire("")
      } else {
        loadQuestionnaireText(res.data)
      }
    } catch (error) {
      console.error("Error Fetching Questionnaire Data:", error)
      notify.error(
        "Error fetching questionnaire",
        "Please try again later."
      )
    }
  }

  /** The questionnaire as the server holds it now (to tell what a failed checkpoint saved). */
  const readSavedText = useCallback(
    async (): Promise<string> =>
      String((await getQuestionnaireSilent(project.id, project.Created_by)).data ?? ""),
    [getQuestionnaireSilent, project.id, project.Created_by]
  )

  /**
   * Saves the current state of the questionnaire, including any selected variant.
   *
   * @async
   * @function saveQuestionnaire
   * @param updatedVariantsParam - Optional updated variants array
   * @param collaborativeContent - Optional pre-built content from collaborative editor
   * @param options - `checkpoint` for a save that cuts a version (a manual save, a restore)
   * @returns Promise that resolves when the questionnaire is successfully saved
   */
  const saveQuestionnaire = useCallback(
    async (
      updatedVariantsParam?: Variant[],
      collaborativeContent?: string,
      options?: EditorSaveOptions
    ): Promise<void> => {
      const variantsToUse: Variant[] = updatedVariantsParam || variants

      // If collaborative content is provided, use it directly
      if (collaborativeContent) {
        try {
          const saveRes = await saveQuestionnaireByID(
            project.id,
            collaborativeContent,
            project.Created_by,
            { checkpoint: options?.checkpoint }
          )

          const saveSuccess = handleSequenceError(saveRes.data, "Error Saving Questionnaire!")
          // Already reported, with the server's message
          if (!saveSuccess) throw new HandledApiError("The questionnaire wasn't saved")

          // Keep local state in sync with what we just saved
          // This ensures variant auto-saves and non-collaborative saves use fresh data
          setFullQuestionnaire(collaborativeContent)

          // Extract and update master content
          const masterContentLines: string[] = []
          let insideVariant = false
          collaborativeContent.split("\n").forEach((line: string) => {
            if (line.startsWith("#DEFINE VARIANT")) {
              insideVariant = true
            } else if (!insideVariant) {
              masterContentLines.push(line)
            }
          })
          setMasterQuestionnaire(masterContentLines.join("\n").trim())

          // Sync variant metadata and content for collaboration
          const parsedVariants: Variant[] =
            parseQuestionnaireVariant(collaborativeContent)
          setVariants(parsedVariants)
          parsedVariants.forEach((variant: Variant) => {
            updateVariantMetadata(variant.name, variant.languages)
          })
        } catch (error) {
          throw await reportSaveFailure(error, {
            text: collaborativeContent,
            checkpoint: options?.checkpoint,
            readBack: readSavedText,
          })
        }
        return
      }

      // Non-collaborative save - build questionnaire from editor (or, when the editor has
      // closed, from the content it had then)
      const editorHTML: string | undefined =
        options?.editorHTML ?? editorRef.current?.getHTML()
      if (editorHTML !== undefined) {
        // Strip comment spans before saving - they break the backend parser
        const cleanedHTML: string = stripCommentSpans(editorHTML)
        const text: string = cleanedHTML
          .replace(/<p>/g, "")
          .replace(/<\/p>/g, "\n")
          .trim()

        const unescapedText: string = text
          .replace(/&lt;&lt;/g, "<<")
          .replace(/&gt;&gt;/g, ">>")
          .replace(/&lt;/g, "<")
          .replace(/&gt;/g, ">")
          .replace(/&nbsp;/g, " ")
          .replace(/&amp;/g, "&")

        let updatedQuestionnaire: string = masterQuestionnaire
        const selectedVariantWithName = selectedVariant as Variant

        if (selectedVariantWithName.name) {
          updatedQuestionnaire = updateQuestionnaireWithVariant(
            updatedQuestionnaire,
            selectedVariantWithName.name,
            unescapedText,
            selectedVariantWithName.languages
          )
        } else {
          updatedQuestionnaire = unescapedText.trim()
        }

        const variantOrder: Record<string, number> = {}
        variantsToUse.forEach((variant: Variant, index: number) => {
          variantOrder[variant.name] = index
          if (variant.name !== selectedVariantWithName.name) {
            updatedQuestionnaire = updateQuestionnaireWithVariant(
              updatedQuestionnaire,
              variant.name,
              variant.content || "",
              variant.languages
            )
          }
        })

        const parsedVariants: Variant[] =
          parseQuestionnaireVariant(updatedQuestionnaire)
        parsedVariants.sort(
          (a: Variant, b: Variant) =>
            variantOrder[a.name] - variantOrder[b.name]
        )

        updatedQuestionnaire = parsedVariants.reduce(
          (acc: string, variant: Variant) =>
            updateQuestionnaireWithVariant(
              acc,
              variant.name,
              variant.content,
              variant.languages
            ),
          updatedQuestionnaire
        )

        try {
          const saveRes = await saveQuestionnaireByID(
            project.id,
            updatedQuestionnaire,
            project.Created_by,
            { checkpoint: options?.checkpoint }
          )
          const saveSuccess = handleSequenceError(saveRes.data, "Error Saving Questionnaire!")
          // Already reported, with the server's message
          if (!saveSuccess) throw new HandledApiError("The questionnaire wasn't saved")
          setFullQuestionnaire(updatedQuestionnaire)
          setVariants(parsedVariants)

          // Sync variant metadata for collaboration
          // Update metadata for all variants (this handles both new and existing)
          parsedVariants.forEach((variant: Variant) => {
            updateVariantMetadata(variant.name, variant.languages)
          })
        } catch (error) {
          throw await reportSaveFailure(error, {
            text: updatedQuestionnaire,
            checkpoint: options?.checkpoint,
            readBack: readSavedText,
          })
        }
      }
    },
    [
      masterQuestionnaire,
      selectedVariant,
      variants,
      project.id,
      project.Created_by,
      saveQuestionnaireByID,
      updateVariantMetadata,
      readSavedText,
    ]
  )

  /**
   * Handles the deletion of a variant from the questionnaire.
   *
   * @async
   * @function handleDeleteVariant
   * @param variant - The variant object to be deleted
   * @returns Promise that resolves when the variant is successfully deleted
   */
  const handleDeleteVariant = async (variant: Variant): Promise<void> => {
    try {
      if (!fullQuestionnaire) {
        return
      }
      const questionnaireLines: string[] = fullQuestionnaire.split("\n")
      let insideVariant = false
      const updatedQuestionnaireLines: string[] = []

      questionnaireLines.forEach((line: string) => {
        if (line.startsWith(`#DEFINE VARIANT ${variant.name}`)) {
          insideVariant = true
          return
        }
        if (
          insideVariant &&
          (line.startsWith("#DEFINE VARIANT") || line.trim() === "")
        ) {
          insideVariant = false
        }
        if (!insideVariant) {
          updatedQuestionnaireLines.push(line)
        }
      })

      const updatedQuestionnaire: string = updatedQuestionnaireLines
        .join("\n")
        .trim()
      const updatedVariants: Variant[] = variants.filter(
        (v: Variant) => v.name !== variant.name
      )

      setVariants(updatedVariants)
      setFullQuestionnaire(updatedQuestionnaire)
      setMasterQuestionnaire(updatedQuestionnaire)

      await saveQuestionnaireByID(
        project.id,
        updatedQuestionnaire,
        project.Created_by
      )

      // Remove variant metadata for collaboration sync
      removeVariantMetadata(variant.name)

      setSelectedVariant({})
      setVariantContent("")
    } catch (error) {
      console.error("Error Deleting Variant:", error)
      notify.error(
        "An unexpected error occurred.",
        "Please try again later."
      )
    }
  }

  /**
   * Generates the preview content based on the selected languages and variant.
   *
   * @async
   * @function generatePreview
   * @param languages - The selected languages (array for multi-language side-by-side)
   * @param variant - The selected variant
   * @param notes - Optional array of note types to include
   */
  const generatePreview = async (
    languages: string[],
    variant: string,
    notes?: string[]
  ): Promise<void> => {
    const options: ConversionOptions = {
      languages: languages,
      variant: variant,
      notes: notes,
    }

    try {
      showPreview(true)
      try {
        const res = await convertToWordPreview(project.id, options)

        // Type guard for error response
        const hasErrorMessage = (
          data: unknown
        ): data is { "Err msg": string } => {
          return !!(
            data &&
            typeof data === "object" &&
            data !== null &&
            "Err msg" in data &&
            typeof (data as Record<string, unknown>)["Err msg"] === "string"
          )
        }

        if (
          hasErrorMessage(res.data) &&
          res.data["Err msg"].startsWith("Error")
        ) {
          setPreviewContent("An error occurred while generating the preview.")
        } else {
          setPreviewContent(String(res.data))
        }
      } catch (error) {
        console.error("Error Generating Preview:", error)
        setPreviewContent("An error occurred while generating the preview.")
      }
    } catch (error) {
      console.error("Error during generatePreview:", error)
      notify.error(
        "An unexpected error occurred.",
        "Please try again later."
      )
    }
  }

  /**
   * Handles the selection of a variant.
   *
   * @function handleSelectVariant
   * @param variant - The selected variant object
   */
  const handleSelectVariant = (variant: Variant | { name: string }): void => {
    if (variant.name === "Master") {
      setSelectedVariant({})
      setVariantContent("")
    } else {
      const fullVariant = variant as Variant
      setSelectedVariant(fullVariant)
      if (fullVariant.content) {
        setVariantContent(
          fullVariant.content
            .split("\n")
            .map(
              (s: string) => "<p>" + escapeHTMLPreserveFormatting(s) + "</p>"
            )
            .join("")
        )
      } else {
        setVariantContent("<p></p>")
      }
    }
  }

  /**
   * Returns the notes for the entire questionnaire.
   *
   * @function getQuestionnaireNotes
   * @returns Array of notes for the questionnaire
   */
  const getQuestionnaireNotes = (): string[] => {
    const notes: string[] = []
    fullQuestionnaire.split("\n").forEach((line: string) => {
      // Check if the line starts with "NOTE" or "NOTE FOR"
      if (line.startsWith("NOTE")) {
        let note: string
        if (line.startsWith("NOTE FOR")) {
          note = line.split("NOTE FOR")[1].split(":")[0].trim()
        } else {
          note = line.split("NOTE")[1].split(":")[0].trim()
        }

        if (note) {
          notes.push(note)
        }
      }
    })

    return notes
  }

  /**
   * Gets the current content to display in the editor based on the selected variant.
   *
   * @function getEditorContent
   * @returns The content to display in the editor
   */
  const getEditorContent = (): string => {
    const selectedVariantWithName = selectedVariant as Variant
    return selectedVariantWithName.name ? variantContent : questionnaire
  }

  /**
   * Fetches the latest questionnaire from the backend.
   * Used by collaborative save to ensure we merge with fresh data.
   *
   * @async
   * @function fetchLatestQuestionnaire
   * @returns Promise that resolves with the latest questionnaire content
   */
  const fetchLatestQuestionnaire = useCallback(async (): Promise<string> => {
    try {
      // Use silent fetch to avoid triggering loading state
      const res = await getQuestionnaireSilent(project.id, project.Created_by)
      return res.data || ""
    } catch (error) {
      console.error("Error fetching latest questionnaire:", error)
      return fullQuestionnaire // Fall back to current state
    }
  }, [
    getQuestionnaireSilent,
    project.id,
    project.Created_by,
    fullQuestionnaire,
  ])

  /**
   * Handles saving current content before switching variants.
   * This ensures content is persisted to backend before variant switch.
   *
   * @async
   * @function handleBeforeVariantSwitch
   * @returns Promise that resolves when save is complete
   */
  const handleBeforeVariantSwitch = useCallback(async (): Promise<void> => {
    if (!editorRef.current) return

    try {
      // Get current editor content
      const editorHTML = editorRef.current.getHTML()
      const currentTextContent = htmlToPlainText(editorHTML)

      // Fetch latest from backend
      const latestQuestionnaire = await fetchLatestQuestionnaire()

      // Build the collaborative content
      const selectedVariantWithName = selectedVariant as Variant
      const currentVariantName = selectedVariantWithName.name || ""

      let result = ""
      if (currentVariantName === "") {
        // Editing master
        result = currentTextContent
      } else {
        // Editing a variant - extract master from latest
        const masterContentLines: string[] = []
        let insideVariant = false
        latestQuestionnaire.split("\n").forEach((line: string) => {
          if (line.startsWith("#DEFINE VARIANT")) {
            insideVariant = true
          } else if (insideVariant && line.trim() === "") {
            return
          } else if (insideVariant && !line.startsWith("#")) {
            return
          } else if (!insideVariant) {
            masterContentLines.push(line)
          }
        })
        result = masterContentLines.join("\n").trim()
      }

      // Parse fresh variants from backend
      const freshVariants = parseQuestionnaireVariant(latestQuestionnaire)
      const freshVariantMap = new Map(freshVariants.map((v) => [v.name, v]))

      // Build full questionnaire with all variants
      variants.forEach((variant: Variant) => {
        let variantContent = ""
        if (variant.name === currentVariantName) {
          variantContent = currentTextContent
        } else {
          const freshVariant = freshVariantMap.get(variant.name)
          variantContent = freshVariant?.content || variant.content || ""
        }
        if (variantContent) {
          result = updateQuestionnaireWithVariant(
            result,
            variant.name,
            variantContent,
            variant.languages
          )
        }
      })

      // Save to backend
      await saveQuestionnaireByID(project.id, result, project.Created_by)
    } catch (error) {
      console.error("Error saving before variant switch:", error)
    }
  }, [
    editorRef,
    fetchLatestQuestionnaire,
    selectedVariant,
    variants,
    saveQuestionnaireByID,
    project.id,
    project.Created_by,
  ])

  /**
   * Inserts text at the end of the editor's content.
   *
   * @function insertEditorText
   * @param text - The text to insert
   */
  const insertEditorText = (text: string): void => {
    if (editorRef.current) {
      editorRef.current.insertContentAtEnd(text)
    }
  }

  /**
   * Opens the labels modal and optionally navigates to a specific label
   *
   * @function openLabelsModal
   * @param labelName - Optional label name to navigate to
   */
  const openLabelsModal = useCallback((labelName?: string): void => {
    setLabelToJump(labelName || null)
    setIsLabelsModalVisible(true)
  }, [])

  if (isLoadingQuestionnaire || selectedVariant === null) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <Loader fontSize={48} />
        Loading Questionnaire...
      </div>
    )
  }

  const sidePaneVisible: boolean = assistantOpen || previewVisible

  return (
    <AssistantScreenProvider projectId={project.id} owner={project.Created_by}>
      <AssistantScreenRegistrar registration={assistantRegistration} />
      <div className="flex flex-col h-full overflow-hidden rounded-xl">
        <div className="flex flex-col flex-grow overflow-hidden">
          <Split
            className="flex flex-grow overflow-hidden"
            gutterSize={sidePaneVisible ? 5 : 0}
            sizes={assistantOpen ? [65, 35] : previewVisible ? [50, 50] : [111, 0]}
          >
            <Editor
              ref={editorRef}
              questionnaire={getEditorContent()}
              saveQuestionnaire={saveQuestionnaire}
              variants={variants}
              masterLanguages={masterLanguages}
              inMaster={inMaster}
              getQuestionnaireNotes={getQuestionnaireNotes}
              onOpenLabelsModal={openLabelsModal}
              labelsButtonRef={labelsButtonRef}
              projectId={project.id}
              userId={user.username}
              owner={project.Created_by}
              currentVariant={(selectedVariant as Variant).name || ""}
              fullQuestionnaire={fullQuestionnaire}
              loadQuestionnaireText={loadQuestionnaireText}
              fetchLatestQuestionnaire={fetchLatestQuestionnaire}
              onSwitchVariant={handleSelectVariant}
              pendingScrollLine={pendingScrollLine}
              onClearPendingScroll={() => setPendingScrollLine(null)}
              onSetPendingScroll={setPendingScrollLine}
              checkingQuestionnaireEnabled={checkingQuestionnaireEnabled}
              setCheckingQuestionnaireEnabled={setCheckingQuestionnaireEnabled}
              checkQuestionnaireErrors={checkQuestionnaireErrors}
              setCheckQuestionnaireErrors={setCheckQuestionnaireErrors}
              checkQuestionnaireWarnings={checkQuestionnaireWarnings}
              setCheckQuestionnaireWarnings={setCheckQuestionnaireWarnings}
              onConnectionStateChange={setIsEditorConnecting}
              templateProposalRef={templateProposalRef}
              toolbarExtras={
                <VariantsMenuBar
                  variants={variants}
                  setVariants={setVariants}
                  selectedVariant={selectedVariant}
                  setSelectedVariant={handleSelectVariant}
                  generatePreview={generatePreview}
                  getQuestionnaireNotes={getQuestionnaireNotes}
                  refresh={getQuestionnaire}
                  insertVariantText={insertEditorText}
                  setPreviewVisible={showPreview}
                  handleDeleteVariant={handleDeleteVariant}
                  saveQuestionnaire={saveQuestionnaire}
                  editorRef={editorRef}
                  scrollToLine={(lineNumber: number) =>
                    editorRef.current?.scrollToLine(lineNumber)
                  }
                  isLabelsModalVisible={isLabelsModalVisible}
                  onLabelsModalVisibilityChange={setIsLabelsModalVisible}
                  labelToJump={labelToJump}
                  onJumpComplete={() => setLabelToJump(null)}
                  labelsButtonRef={labelsButtonRef}
                  collaborativeMode={true}
                  onBeforeVariantSwitch={handleBeforeVariantSwitch}
                  isConnecting={isEditorConnecting}
                />
              }
            />
            {assistantOpen ? (
              <div className="h-full min-h-0 border-l border-gray-200 bg-white">
                <AssistantPanel />
              </div>
            ) : previewVisible ? (
              <PreviewQuestionnaire
                previewContent={previewContent}
                isConvertingToWordPreview={isConvertingToWordPreview}
                setPreviewVisible={setPreviewVisible}
              />
            ) : (
              <div className="hidden" />
            )}
          </Split>
        </div>
      </div>
      <QnrProposalReview
        proposal={reviewing}
        projectId={project.id}
        currentVariant={currentVariant}
        getEditor={getTiptapEditor}
        onClose={() => setReviewing(null)}
      />
    </AssistantScreenProvider>
  )
}

export default QuestionnaireEditorContainer
